import { useState } from "react";
import { Check, Copy, TriangleAlert } from "lucide-react";
import { toast } from "sonner";

import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";

import type {
  AccessKeyExpiration,
  AccessKeyRole,
} from "@/hooks/use-access-keys";

/** A key just issued: its secret plus what the operator submitted. */
export interface IssuedKey {
  secret: string;
  tenantName: string;
  name: string;
  owner: string;
  role: AccessKeyRole;
  expiration: AccessKeyExpiration;
}

interface SecretDialogProps {
  issued: IssuedKey | null;
  onClose: () => void;
}

/** Only "Done" closes it: a stray click or Escape would lose the secret for good. */
export function SecretDialog({ issued, onClose }: SecretDialogProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    if (!issued) return;
    try {
      await navigator.clipboard.writeText(issued.secret);
      setCopied(true);
      toast.success("Key copied to clipboard");
    } catch {
      toast.error("Could not copy; select the key and copy it manually");
    }
  };

  const close = () => {
    setCopied(false);
    onClose();
  };

  return (
    <Dialog open={issued !== null}>
      <DialogContent
        className="sm:max-w-xl"
        showCloseButton={false}
        onInteractOutside={(event) => {
          event.preventDefault();
        }}
        onEscapeKeyDown={(event) => {
          event.preventDefault();
        }}
      >
        <DialogHeader>
          <DialogTitle>API key created</DialogTitle>
          <DialogDescription>
            Hand this key to its owner over a secure channel. It is shown only
            now: Harmony does not store it and cannot show it again.
          </DialogDescription>
        </DialogHeader>

        {issued ? (
          <div className="space-y-4">
            <div className="flex gap-2">
              <Input
                readOnly
                aria-label="API key"
                value={issued.secret}
                className="font-mono text-xs"
              />
              <Button
                variant="outline"
                onClick={() => {
                  void handleCopy();
                }}
              >
                {copied ? (
                  <Check className="size-4" aria-hidden />
                ) : (
                  <Copy className="size-4" aria-hidden />
                )}
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>

            <dl className="grid grid-cols-[auto_1fr] gap-x-4 gap-y-1 text-sm">
              <dt className="text-muted-foreground">Name</dt>
              <dd>{issued.name}</dd>
              <dt className="text-muted-foreground">Tenant</dt>
              <dd>{issued.tenantName}</dd>
              <dt className="text-muted-foreground">Owner</dt>
              <dd>{issued.owner}</dd>
              <dt className="text-muted-foreground">Access</dt>
              <dd>{issued.role}</dd>
              <dt className="text-muted-foreground">Expires in</dt>
              <dd>{issued.expiration}</dd>
            </dl>

            <p className="text-muted-foreground flex items-start gap-2 text-xs">
              <TriangleAlert className="mt-0.5 size-3.5 shrink-0" aria-hidden />
              Close this dialog only after the key is delivered. If it is lost,
              create a new key and delete this one.
            </p>
          </div>
        ) : null}

        <DialogFooter>
          <Button onClick={close}>Done</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
